// src/Views/IntegrationsView.jsx
import React, { useState } from 'react';
import PageHeader from '../components/Common/PageHeader';

const IntegrationsView = ({ showNotification }) => {
  const [integrations, setIntegrations] = useState([
    {
      id: 'INT001',
      name: 'Google Calendar',
      status: 'connected',
      description: 'Sync appointments with Google Calendar',
      lastSync: '2 hours ago',
      syncCount: 1247,
      category: 'Calendar',
      provider: 'Google',
      icon: '📅',
      features: ['Two-way sync', 'Automated reminders', 'Meeting links']
    },
    {
      id: 'INT002',
      name: 'Stripe Payments',
      status: 'connected',
      description: 'Process payments securely with Stripe',
      lastSync: '5 minutes ago',
      syncCount: 456,
      category: 'Payments',
      provider: 'Stripe',
      icon: '💳',
      features: ['Credit card processing', 'Automated receipts', 'Refund management']
    },
    {
      id: 'INT003',
      name: 'Mailchimp',
      status: 'available',
      description: 'Email marketing and automation platform',
      lastSync: 'Never',
      syncCount: 0,
      category: 'Marketing',
      provider: 'Mailchimp',
      icon: '📧',
      features: ['Email campaigns', 'Customer segmentation', 'Automated follow-ups']
    },
    {
      id: 'INT004',
      name: 'Zoom',
      status: 'available',
      description: 'Video consultation integration for remote appointments',
      lastSync: 'Never',
      syncCount: 0,
      category: 'Communication',
      provider: 'Zoom',
      icon: '📹',
      features: ['Video consultations', 'Meeting recordings', 'Waiting rooms']
    },
    {
      id: 'INT005',
      name: 'Microsoft Outlook',
      status: 'available',
      description: 'Sync appointments with Microsoft Outlook calendar',
      lastSync: 'Never',
      syncCount: 0,
      category: 'Calendar',
      provider: 'Microsoft',
      icon: '🗓️',
      features: ['Calendar sync', 'Email notifications', 'Meeting invites']
    }
  ]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter integrations based on category and search
  const filteredIntegrations = integrations.filter(integration => {
    const matchesCategory = filterCategory === 'all' || integration.category === filterCategory;
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integration.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get unique categories for filter
  const categories = ['all', ...new Set(integrations.map(int => int.category))];

  const handleBrowseAvailable = () => {
    showNotification('Opening integration marketplace...', 'info');
    // This would typically open a modal or navigate to a marketplace
    setTimeout(() => {
      showNotification('Integration marketplace opened! Browse available integrations.', 'success');
    }, 1000);
  };

  const handleConnect = async (integrationId) => {
    const integration = integrations.find(int => int.id === integrationId);
    if (!integration) return;

    showNotification(`Connecting to ${integration.name}...`, 'info');

    // Simulate connection delay
    setTimeout(() => {
      const updatedIntegrations = integrations.map(int => 
        int.id === integrationId 
          ? { 
              ...int, 
              status: 'connected', 
              lastSync: 'Just now',
              setupDate: new Date().toISOString().split('T')[0],
              syncCount: Math.floor(Math.random() * 100) + 1
            }
          : int
      );
      
      setIntegrations(updatedIntegrations);
      showNotification(`${integration.name} connected successfully!`, 'success');
    }, 2000);
  };

  const handleDisconnect = (integrationId) => {
    const integration = integrations.find(int => int.id === integrationId);
    if (!integration) return;

    showNotification(`Disconnecting ${integration.name}...`, 'warning');

    setTimeout(() => {
      const updatedIntegrations = integrations.map(int => 
        int.id === integrationId 
          ? { 
              ...int, 
              status: 'available', 
              lastSync: 'Never',
              setupDate: null,
              syncCount: 0
            }
          : int
      );
      
      setIntegrations(updatedIntegrations);
      showNotification(`${integration.name} disconnected`, 'warning');
    }, 1000);
  };

  const handleConfigure = (integrationId) => {
    const integration = integrations.find(int => int.id === integrationId);
    if (!integration) return;
    
    showNotification(`Opening ${integration.name} configuration...`, 'info');
    setTimeout(() => {
      showNotification(`${integration.name} configuration panel opened`, 'success');
    }, 500);
  };

  const handleSync = (integrationId) => {
    const integration = integrations.find(int => int.id === integrationId);
    if (!integration) return;

    showNotification(`Syncing ${integration.name}...`, 'info');

    setTimeout(() => {
      const updatedIntegrations = integrations.map(int => 
        int.id === integrationId 
          ? { 
              ...int, 
              lastSync: 'Just now',
              syncCount: (int.syncCount || 0) + Math.floor(Math.random() * 10) + 1
            }
          : int
      );
      
      setIntegrations(updatedIntegrations);
      showNotification(`${integration.name} synced successfully!`, 'success');
    }, 1500);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'connected':
        return 'status-active';
      case 'available':
        return 'status-pending';
      case 'error':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const renderIntegrationCard = (integration) => (
    <div key={integration.id} className="integration-card">
      <div className="integration-header">
        <div className="integration-info">
          <div className="integration-icon">{integration.icon}</div>
          <div className="integration-details">
            <div className="integration-name">{integration.name}</div>
            <div className="integration-description">{integration.description}</div>
            {integration.status === 'connected' && (
              <div className="integration-sync">
                Last sync: {integration.lastSync} • {integration.syncCount} items
              </div>
            )}
            {integration.features && integration.features.length > 0 && (
              <div className="integration-features">
                {integration.features.slice(0, 3).map((feature, index) => (
                  <span key={index} className="feature-tag">{feature}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        <span className={`status-badge ${getStatusBadgeClass(integration.status)}`}>
          {integration.status}
        </span>
      </div>
      
      <div className="integration-actions">
        {integration.status === 'connected' ? (
          <>
            <button 
              className="btn btn-secondary"
              onClick={() => handleConfigure(integration.id)}
            >
              Configure
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => handleSync(integration.id)}
            >
              Sync Now
            </button>
            <button 
              className="btn btn-danger btn-sm"
              onClick={() => handleDisconnect(integration.id)}
            >
              Disconnect
            </button>
          </>
        ) : (
          <button 
            className="btn btn-primary"
            onClick={() => handleConnect(integration.id)}
          >
            Connect
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="integrations-view">
      {/* Page Header */}
      <PageHeader pageKey="integrations">
        <button className="btn btn-secondary" onClick={handleBrowseAvailable}>
          <span className="icon icon--search icon--default"></span>
          Browse Available
        </button>
      </PageHeader>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Available Integrations</h3>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="filter-group">
            <input
              type="text"
              className="search-input"
              placeholder="Search integrations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select
              className="filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="card-content">
          <div className="integrations-grid">
            {filteredIntegrations.length > 0 ? (
              filteredIntegrations.map(renderIntegrationCard)
            ) : integrations.length === 0 ? (
              <div className="no-integrations">
                <p>Loading integrations...</p>
              </div>
            ) : (
              <div className="no-integrations">
                <p>No integrations found matching your criteria.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsView;